// src/lib/analytics/consent-mode.ts
/**
 * @fileoverview Google Consent Mode v2 bootstrap.
 * @description Builds the single synchronous `<head>` inline script that MUST
 * run before `gtag.js` so Consent Mode is fully resolved before any Google tag
 * fires. It is deliberately ALL synchronous and in a fixed order so there is no
 * timing race (an earlier split that deferred the stored-preference replay to
 * `afterInteractive` could let `config` initialize under the wrong consent, and
 * could miss the `wait_for_update` window):
 *
 *   1. `gtag` stub (so our own pageview/event calls queue into `dataLayer`)
 *   2. region-scoped default — EEA/UK/CH DENIED until opt-in (most-specific wins)
 *   3. global default — GRANTED for the rest of the world; a Global Privacy
 *      Control signal (`navigator.globalPrivacyControl`) denies only the
 *      ad_* and personalization (sale-share) signals at the Google-tag layer, while
 *      first-party `analytics_storage` stays granted (GPC is a sale/share
 *      opt-out, not an opt-out of first-party measurement)
 *   4. replay of a returning visitor's explicit stored choice (overrides the
 *      default) — BEFORE `config`, so a stored opt-out is applied before
 *      `gtag.js` drains `config`
 *   5. `gtag('js')` + `config` with `send_page_view:false`
 *
 * Performance: ~0.6 KB, no network/layout work; one synchronous `localStorage`
 * read. Kept in `<head>` deliberately — an interleaved A/B benchmark showed
 * moving it off the synchronous path yields no real win on this app (the
 * canvas-page TBT is unchanged), so correctness wins. See scripts/ab-bench.ts
 * and docs/perf/benchmarking-methodology.md.
 */

import { CONSENT_STORAGE_KEY } from "./consent"
import {
  ANALYTICS_COOKIE_MAX_AGE_SEC,
  ATTRIBUTION_COOKIE_NAME,
  CONSENT_ANALYTICS_EXPLICIT_COOKIE,
  CONSENT_ANALYTICS_MIRROR_COOKIE,
} from "./cookie-names"
// The region list now lives in `./region` (shared with the edge middleware +
// server checkout gate, which must not import this browser bootstrap builder).
// Re-exported here for back-compat with existing importers/tests.
import { CONSENT_DENIED_BY_DEFAULT_REGIONS } from "./region"

export { CONSENT_DENIED_BY_DEFAULT_REGIONS }

/** Consent Mode object literal for a fully granted state. */
const GRANTED =
  "{ad_storage:'granted',ad_user_data:'granted',ad_personalization:'granted'," +
  "analytics_storage:'granted',personalization_storage:'granted'," +
  "functionality_storage:'granted',security_storage:'granted'}"

/**
 * Consent Mode object literal for a Global Privacy Control (do-not-sell/share)
 * visitor: the ad_* and personalization (sale-share) signals are denied, but
 * first-party `analytics_storage` stays GRANTED. GPC is a sale/share opt-out,
 * not an opt-out of first-party measurement.
 */
const GPC_SALE_SHARE_DENIED =
  "{ad_storage:'denied',ad_user_data:'denied',ad_personalization:'denied'," +
  "analytics_storage:'granted',personalization_storage:'denied'," +
  "functionality_storage:'granted',security_storage:'granted'}"

/**
 * Build the synchronous `<head>` Consent Mode v2 bootstrap.
 *
 * @param measurementId - GA4 measurement id (`G-XXXXXXX`).
 * @param options.debug - Emit `debug_mode: true` for GA4 DebugView.
 * @returns Inline JS string for the `<head>` `<script>`.
 */
export function buildConsentBootstrap(
  measurementId: string,
  options: { debug?: boolean } = {},
): string {
  const regions = JSON.stringify(CONSENT_DENIED_BY_DEFAULT_REGIONS)
  const debugLine = options.debug ? ",debug_mode:true" : ""
  // The region-scoped default carries wait_for_update for a downstream CMP.
  const regionDenied =
    "{ad_storage:'denied',ad_user_data:'denied',ad_personalization:'denied'," +
    "analytics_storage:'denied',personalization_storage:'denied'," +
    `functionality_storage:'granted',security_storage:'granted',wait_for_update:500,region:${regions}}`
  const mirrorMaxAge = ANALYTICS_COOKIE_MAX_AGE_SEC
  const mirrorCookie = CONSENT_ANALYTICS_MIRROR_COOKIE
  const explicitCookie = CONSENT_ANALYTICS_EXPLICIT_COOKIE
  const attrCookie = ATTRIBUTION_COOKIE_NAME
  return (
    "window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}" +
    // (1) Global Privacy Control, read synchronously.
    "var __gpc=(typeof navigator!=='undefined'&&navigator.globalPrivacyControl===true);" +
    // Region cookie from middleware (same response) — EEA default-deny.
    "var __eea=/(?:^|;\\s*)adsights_region_cr=1(?:;|$)/.test(typeof document!=='undefined'?document.cookie:'');" +
    `function __mirror(v){try{var s=location.protocol==='https:'?'; Secure':'';document.cookie='${mirrorCookie}='+v+'; Path=/; Max-Age=${mirrorMaxAge}; SameSite=Lax'+s;}catch(e){}}` +
    `function __explicit(v){try{var s=location.protocol==='https:'?'; Secure':'';document.cookie='${explicitCookie}='+v+'; Path=/; Max-Age=${mirrorMaxAge}; SameSite=Lax'+s;}catch(e){}}` +
    `function __clearAttr(){try{var s=location.protocol==='https:'?'; Secure':'';document.cookie='${attrCookie}=; Path=/; Max-Age=0; SameSite=Lax'+s;}catch(e){}}` +
    `function __explicitGrant(){try{var h=document.cookie;if(/(?:^|;\\s*)${explicitCookie}=1(?:;|$)/.test(h))return true;if(/(?:^|;\\s*)${explicitCookie}=0(?:;|$)/.test(h))return false;}catch(e){}return null;}` +
    `function __replayExplicitCookie(){var eg=__explicitGrant();if(eg===null)return false;var a=eg?'granted':'denied';gtag('consent','update',{analytics_storage:a,ad_storage:'denied',ad_user_data:'denied',ad_personalization:'denied',personalization_storage:'denied',functionality_storage:'granted'});__mirror(eg?'1':'0');__explicit(eg?'1':'0');if(!eg)__clearAttr();return true;}` +
    // (2) Region-scoped default: EEA/UK/CH denied until opt-in.
    `gtag('consent','default',${regionDenied});` +
    // (3) Global default: granted for rest of world. A GPC signal denies only
    // the ad_*/personalization (sale-share) signals; first-party
    // analytics_storage stays granted.
    `gtag('consent','default',__gpc?${GPC_SALE_SHARE_DENIED}:${GRANTED});` +
    // (4) Replay a returning visitor's explicit stored choice, BEFORE config.
    `try{var c=localStorage.getItem('${CONSENT_STORAGE_KEY}');if(c){var p=JSON.parse(c);` +
    // GPC is a "do not sell/share" signal, so it overrides a stored opt-in for
    // the AD/sale-share signals (ad_*/personalization) even when ads were
    // previously granted — but a stored ANALYTICS opt-in is respected (GPC does
    // not govern first-party analytics_storage).
    "var a=p.analytics?'granted':'denied';var d=(p.ads&&!p.doNotSellOrShare&&!__gpc)?'granted':'denied';" +
    "gtag('consent','update',{analytics_storage:a,ad_storage:d,ad_user_data:d,ad_personalization:d,personalization_storage:d,functionality_storage:p.functional?'granted':'denied'});" +
    "__mirror(p.analytics?'1':'0');__explicit(p.analytics?'1':'0');if(!p.analytics)__clearAttr();}else if(__replayExplicitCookie()){}else if(__eea){__mirror('0');__clearAttr();}}catch(e){if(!__replayExplicitCookie()){if(__eea){__mirror('0');__clearAttr();}}}" +
    // (5) Init.
    `gtag('js',new Date());gtag('config','${measurementId}',{send_page_view:false${debugLine}});`
  )
}
